import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Progress } from '../components/ui/progress';
import { 
  Leaf, Clock, Pill, AlertTriangle, CheckCircle2,
  Moon, Zap, Shield, Brain, Heart, Scale, Utensils, 
  Bone, Sparkles, Smile, Loader2, Info, Calendar, 
  Coffee, Sun, Sunset, Share2, AlertCircle
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  sleep: Moon,
  energy: Zap,
  immunity: Shield,
  stress: Brain,
  weight: Scale,
  heart: Heart,
  digestion: Utensils,
  joints: Bone,
  hair_skin: Sparkles,
  brain_focus: Brain,
  mood: Smile
};

const categoryColors = {
  sleep: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  energy: 'bg-amber-100 text-amber-600 border-amber-200',
  immunity: 'bg-green-100 text-green-600 border-green-200',
  stress: 'bg-purple-100 text-purple-600 border-purple-200',
  weight: 'bg-rose-100 text-rose-600 border-rose-200',
  heart: 'bg-red-100 text-red-600 border-red-200',
  digestion: 'bg-orange-100 text-orange-600 border-orange-200',
  joints: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  hair_skin: 'bg-pink-100 text-pink-600 border-pink-200',
  brain_focus: 'bg-violet-100 text-violet-600 border-violet-200',
  mood: 'bg-yellow-100 text-yellow-600 border-yellow-200'
};

const priorityColors = {
  essential: 'bg-primary text-white',
  recommended: 'bg-secondary/20 text-secondary',
  optional: 'bg-muted text-muted-foreground'
};

function getTimingIcon(timing) {
  const t = timing.toLowerCase();
  if (t.includes('morning') || t.includes('breakfast')) return Sun;
  if (t.includes('afternoon') || t.includes('lunch')) return Coffee;
  if (t.includes('evening') || t.includes('dinner')) return Sunset;
  if (t.includes('bed') || t.includes('night')) return Moon;
  return Clock;
}

export default function SharedResultsPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedAssessment = async () => {
      try {
        const response = await axios.get(`${API}/shared/${token}`);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching shared assessment:', err);
        if (err.response?.status === 410) {
          setError('This share link has expired.');
        } else if (err.response?.status === 404) {
          setError('Share link not found.');
        } else {
          setError('Failed to load shared results.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedAssessment();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Link Unavailable</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Go to Vitala
          </Button>
        </div>
      </div>
    );
  }

  const { assessment, shared_by, expires_at } = data;
  const sortedScores = Object.entries(assessment.health_scores).sort(([, a], [, b]) => b - a);
  const hasInteractions = assessment.drug_interactions && assessment.drug_interactions.length > 0;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A]"
          >
            <Logo size="default" linkTo={null} className="pointer-events-none" />
          </button>
          <Button onClick={() => navigate('/auth?mode=register')} className="btn-primary">
            Get Your Own Plan
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Shared Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 rounded-2xl bg-primary/5 border border-primary/20"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Share2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#1A1A1A]">
                Shared Supplement Protocol
              </h3>
              <p className="text-sm text-muted-foreground">
                Shared by <span className="font-medium text-primary">{shared_by}</span> • 
                Expires {new Date(expires_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Results Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            Supplement Plan
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Personalized supplement recommendations based on health assessment.
          </p>
        </motion.div>

        {/* Medication Interactions Alert */}
        {hasInteractions && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 p-6 rounded-2xl bg-amber-50 border border-amber-200"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-900 mb-2">
                  Medication-Supplement Interactions
                </h3>
                <div className="space-y-3">
                  {assessment.drug_interactions.map((interaction, i) => (
                    <div 
                      key={i}
                      className={`p-4 rounded-lg ${
                        interaction.interaction_type === 'avoid' 
                          ? 'bg-red-50 border border-red-200' 
                          : interaction.interaction_type === 'caution'
                          ? 'bg-amber-100/50 border border-amber-300'
                          : 'bg-blue-50 border border-blue-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${
                          interaction.interaction_type === 'avoid' 
                            ? 'bg-red-200 text-red-800' 
                            : interaction.interaction_type === 'caution'
                            ? 'bg-amber-200 text-amber-800'
                            : 'bg-blue-200 text-blue-800'
                        }`}>
                          {interaction.interaction_type}
                        </span>
                        <span className="text-sm font-medium text-[#1A1A1A]">
                          {interaction.medication} + {interaction.supplement}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{interaction.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Health Scores & Supplements */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* AI Recommendation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-2 p-8 rounded-2xl bg-white border border-border/50"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-[#1A1A1A]">Health Insights</h2>
            </div>
            <div className="prose prose-sm max-w-none text-[#1A1A1A]/80">
              {assessment.ai_recommendation.split('\n').map((p, i) => (
                p.trim() && <p key={i} className="mb-4">{p}</p>
              ))}
            </div>
          </motion.div>

          {/* Health Scores */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 rounded-2xl bg-white border border-border/50"
          >
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-6">Health Scores</h2>
            <div className="space-y-4">
              {sortedScores.slice(0, 6).map(([category, score]) => {
                const Icon = categoryIcons[category] || Shield;
                const color = categoryColors[category] || 'bg-gray-100 text-gray-600';
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full ${color.split(' ')[0]} flex items-center justify-center`}>
                          <Icon className="w-3 h-3" />
                        </div>
                        <span className="text-sm font-medium text-[#1A1A1A] capitalize">
                          {category.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="text-sm font-semibold">{score}%</span>
                    </div>
                    <Progress value={score} className="h-2" />
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Supplements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <h2 className="text-2xl font-semibold text-[#1A1A1A] mb-6">Supplement Protocol</h2>
          <div className="space-y-4">
            {assessment.recommended_supplements.map((supplement, index) => {
              const Icon = categoryIcons[supplement.category] || Shield;
              const color = categoryColors[supplement.category] || 'bg-gray-100 text-gray-600';
              const TimingIcon = getTimingIcon(supplement.timing);
              
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-border/50 overflow-hidden"
                >
                  <Accordion type="single" collapsible>
                    <AccordionItem value={`item-${index}`} className="border-0">
                      <AccordionTrigger className="px-6 py-5 hover:no-underline">
                        <div className="flex items-center gap-4 w-full">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${priorityColors[supplement.priority]}`}>
                              {supplement.priority}
                            </span>
                            <div className={`w-10 h-10 rounded-full ${color.split(' ')[0]} flex items-center justify-center`}>
                              <Icon className="w-5 h-5" />
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="text-lg font-semibold text-[#1A1A1A]">{supplement.name}</h3>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1 font-medium text-primary">
                                <Pill className="w-4 h-4" />
                                {supplement.dosage}
                              </span>
                              <span className="flex items-center gap-1">
                                <TimingIcon className="w-4 h-4" />
                                {supplement.timing}
                              </span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Info className="w-4 h-4 text-primary" />
                                How to Take
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.how_to_take}</p>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-primary" />
                                Duration
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.duration}</p>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                Benefits
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {(Array.isArray(supplement.benefits) 
                                  ? supplement.benefits 
                                  : (supplement.benefits || '').split(';').map(b => b.trim()).filter(b => b)
                                ).map((b, i) => (
                                  <span key={i} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">{b}</span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-primary" />
                                What to Look For
                              </h4>
                              <p className="text-sm text-muted-foreground">{supplement.what_to_look_for}</p>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center p-12 rounded-2xl bg-primary"
        >
          <h3 className="text-2xl font-semibold text-white mb-4">
            Want Your Own Personalized Plan?
          </h3>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Take our free health assessment and get AI-powered supplement recommendations tailored to your unique needs.
          </p>
          <Button
            onClick={() => navigate('/auth?mode=register&redirect=/quiz')}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-3"
          >
            Get Started Free
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
